import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle, faUser, faUsers } from "@fortawesome/free-solid-svg-icons";
import { BsCheck2, BsX } from "react-icons/bs";
import { toast } from "react-toastify";

// Loading Skeleton
const CardSkeleton = () => (
  <div className="pricing-title-header animate-pulse">
    <div className="h-8 w-24 bg-gray-200 rounded mb-4"></div>
    <div className="h-12 w-32 bg-gray-200 rounded mb-4"></div>
    <div className="h-10 w-40 bg-gray-200 rounded"></div>
  </div>
);
const PriceSkt = () => (
  <div className="pricing-title-header animate-pulse">
    <div className="h-4 w-24 bg-gray-200 rounded mb-4"></div>
    <div className="h-6 w-32 bg-gray-200 rounded mb-4"></div>
    <div className="h-8 w-40 bg-gray-200 rounded"></div>
  </div>
);

export default function DynamicPricing() {
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMMonthly, setIsMMonthly] = useState(true);
  const [isOpenTab, setIsOpenTab] = useState(0);
  const [planType, setPlanType] = useState(isOpenTab === 0 ? "user" : "team");
  const [isToggling, setIsToggling] = useState(false);

  // 👇 Your base URL
  const baseUrl = "https://admin.simplifyte.com/api";
  // const baseUrl = "http://192.168.0.139:8000/api"


  useEffect(() => {
    setPlanType(isOpenTab === 0 ? "user" : "team");
  }, [isOpenTab]);

  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `${baseUrl}/plans-website?type=${planType}`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch plans");
        }

        const result = await response.json();

        const data = result?.data?.[planType] || [];

        // ✅ Only required plans
        const allowedPlans = ["Starter", "Pro", "Elite"];

        const filtered = data.filter((plan) =>
          allowedPlans.includes(plan.name.replace(" User", "")),
        );

        setPricingData({ plans: filtered });
      } catch (err) {
        console.error(err);
        setError("Failed to load pricing");
        setPricingData({ plans: [] });
      } finally {
        setLoading(false);
      }
    };

    fetchPricingData();
  }, [planType]);

  const handleTabClick = (index) => {
    setIsOpenTab(index);
    if (index === 1) {
      setIsMMonthly(false);
    } else {
      setIsMMonthly(true);
    }
  };

  const handleToggle = () => {
    setIsToggling(true);
    // Simulate async operation (e.g., price recalculation)
    setTimeout(() => {
      setIsMMonthly((prev) => !prev);
      setIsToggling(false);
    }, 300); // match animation duration
  };

  const getPrice = (plan) => {
    const planName = plan.name.replace(" User", "");
    const isEliteUser = planName === "Elite" && planType === "user";

    let monthlyPrice, yearlyPrice;
    let dividedby_12 = 0;

    if (planName === "Elite") {
      monthlyPrice = plan.monthly_price;
      yearlyPrice = plan.yearly_price;
    } else if (plan.slabs && plan.slabs.length > 0) {
      const slab = plan.slabs[0];
      monthlyPrice = slab.rate_monthly * slab.count;
      yearlyPrice = slab.rate_annual * slab.count;
    } else {
      monthlyPrice = plan.monthly_price;
      // This is when Toggle is off that Value is there (349,429)
      // console.log("WHAT the Price", monthlyPrice);
      yearlyPrice = plan.yearly_price;
      dividedby_12 = (parseFloat(yearlyPrice) / 12).toLocaleString("en-IN", {
        maximumFractionDigits: 0,
      });
      // Now in toggle on value will be there (329,379)
      // console.log("WHAT the Price", dividedby_12);
    }

    // ✅ Pricing logic
    if (planName === "Free") return 0;

    if (planType === "team" && planName === "Elite") {
      return "Custom";
    }

    if (planName === "Elite") {
      return monthlyPrice;
    }

    if (planType === "team") {
      return yearlyPrice;
    }

    return isMMonthly ? monthlyPrice : dividedby_12;
  };
  const getOriginalMonthlyPrice = (plan, planType) => {
    const planName = plan.name.replace(" User", "");

    // Free plan has no strikethrough
    if (planName === "Free") return 0;

    // For User tab (monthly billing mode) – use the raw monthly price
    if (planType === "user") {
      if (planName === "Elite") return plan.monthly_price;
      if (plan.slabs?.length)
        return plan.slabs[0].rate_monthly * plan.slabs[0].count;
      return plan.monthly_price;
    }

    // For Team tab (yearly only) – optional: compute equivalent monthly from yearly price
    if (planType === "team") {
      if (planName === "Elite") return null; // Custom pricing → no strikethrough
      const yearlyPrice =
        plan.yearly_price || plan.slabs?.[0]?.rate_annual * plan.slabs[0].count;
      return yearlyPrice ? yearlyPrice / 12 : null;
    }

    return null;
  };
  // const formatPrice = (price) => {
  //   if (price === "Custom") return "Custom Pricing";
  //   if (!price) return "Billed Annually";

  //   return `₹${Number(price).toLocaleString("en-IN")}`;
  // };
  const formatPrice = (price) => {
    if (price === "Custom") return "Custom Pricing";
    if (!price) return "Billed Annually";

    // Round down to integer and format without decimals
    const integerPrice = Math.floor(price);
    // console.log("in", integerPrice);
    return `₹${integerPrice.toLocaleString("en-IN")}`;
  };
  const getBillingText = () => {
    return isOpenTab === 0 ? "per user/month" : "p.a.";
  };

  if (error && !pricingData) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">⚠️ Error: {error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="title-sticky grid lg:grid-cols-4 md:grid-cols-2 mt-6 gap-[20px]">
      <div className="pricing-title-header">
        <div className="pricing-inner-title mb-3">
          {/* <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                cursor: isOpenTab === 0 ? "pointer" : "not-allowed",
                position: "relative",
                width: "38px",
                height: "19px",
                backgroundColor:
                  isOpenTab === 0
                    ? isMMonthly
                      ? "#a6a8aa"
                      : "#093a5b"
                    : "#d3d3d3",
                borderRadius: "15px",
                opacity: isOpenTab === 0 ? 1 : 0.6,
              }}
              onClick={isOpenTab === 0 ? handleToggle : null}
            >
              <div
                style={{
                  position: "absolute",
                  top: "-5px",
                  left: "4px",
                  transform: isMMonthly ? "translateX(0)" : "translateX(17px)",
                  transition: "transform 0.4s cubic-bezier(.16,1,.3,1)",
                }}
              >
                <FontAwesomeIcon
                  icon={faCircle}
                  style={{ fontSize: "13px", color: "#f4f4f4" }}
                />
              </div>
            </div>
            {isOpenTab === 0 && (
              <span
                style={{
                  display: "block",
                  fontSize: "15px",
                  fontWeight: "600",
                  color: isMMonthly ? "#a6a8aa" : "#093a5b",
                }}
              >
                Billed Yearly
              </span>
            )}
            {isOpenTab === 1 && (
              <span
                style={{
                  fontSize: "15px",
                  color: "#093a5b",
                  fontWeight: "600",
                }}
              >
                Yearly Only
              </span>
            )}
          </div> */}
          <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
            {/* Toggle switch - only for User tab */}
            {isOpenTab === 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  position: "relative",
                  width: "38px",
                  height: "19px",
                  backgroundColor: isMMonthly ? "#a6a8aa" : "#093a5b",
                  borderRadius: "15px",
                }}
                onClick={handleToggle}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "-5px",
                    left: "4px",
                    transform: isMMonthly
                      ? "translateX(0)"
                      : "translateX(17px)",
                    transition: "transform 0.4s cubic-bezier(.16,1,.3,1)",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faCircle}
                    style={{ fontSize: "13px", color: "#f4f4f4" }}
                  />
                </div>
              </div>
            )}

            {/* Text label - different for each tab */}
            {isOpenTab === 0 && (
              <span
                style={{
                  display: "block",
                  fontSize: "15px",
                  fontWeight: "600",
                  color: isMMonthly ? "#a6a8aa" : "#093a5b",
                }}
              >
                Billed Yearly
              </span>
            )}

            {isOpenTab === 1 && (
              <span
                style={{
                  fontSize: "15px",
                  color: "#093a5b",
                  fontWeight: "600",
                }}
              >
                Yearly Only
              </span>
            )}
          </div>
        </div>

        <p className="main-task-title min-h">
          <span className="sub-task">Select preferred</span>
          <span className="sub-task">billing cycle</span>
        </p>

        <ul className="pricing-sub-tab inline-block w-fit mx-auto flex-wrap justify-center text-center bg-white dark:bg-slate-900 shadow dark:shadow-gray-800 rounded-xl w-li">
          <li role="presentation" className="ti-task tab-btn inline-block">
            <button
              onClick={() => handleTabClick(0)}
              className={`${isOpenTab === 0 ? "fifth-tab text-white bg-indigo-600 hover:text-white" : ""} fifth-tab text-sm font-semibold rounded-xl w-full transition-all duration-500 ease-in-out`}
            >
              <FontAwesomeIcon
                icon={faUser}
                className="sub-header-career mr-2"
              />
              User
            </button>
          </li>
          <li role="presentation" className="ti-task tab-btn inline-block">
            <button
              onClick={() => handleTabClick(1)}
              className={`${isOpenTab === 1 ? "fifth-tab text-white bg-indigo-600 hover:text-white" : ""} fifth-tab text-sm font-semibold rounded-xl w-full transition-all duration-500 ease-in-out`}
            >
              <FontAwesomeIcon
                icon={faUsers}
                className="sub-header-career mr-2"
              />
              Team
            </button>
          </li>
        </ul>
      </div>

      {/* Pricing Cards */}
      {loading
        ? Array.from({ length: 3 }).map((_, idx) => <CardSkeleton key={idx} />)
        : pricingData?.plans?.map((plan) => {
            const planName = plan.name.replace(" User", "");
            const isEliteUser = planName === "Elite" && planType === "user";
            const currentPrice = getPrice(plan);
            const originalMonthlyPrice = getOriginalMonthlyPrice(
              plan,
              planType,
            );
            const showStrikethrough =
              planType === "user" &&
              !isMMonthly &&
              originalMonthlyPrice > currentPrice;

            // const price = getPrice(plan); // ✅ optimization

            return (
              <>
                {isToggling ? (
                  <PriceSkt />
                ) : (
                  <div key={plan.id} className="pricing-title-header">
                    <h6
                      className={`font-bold uppercase mb-6 text-indigo-600 stickey-title ${plan.is_popular ? "relative" : ""}`}
                    >
                      {/* {plan.name} */}
                      {plan.name.replace(" User", "")}
                      {plan.is_popular && (
                        <span className="ml-2 text-xs bg-indigo-100 text-indigo-600 px-2 py-1 rounded-full">
                          Popular
                        </span>
                      )}
                    </h6>

                    <p className="main-task-title min-h">
                      <span className="flex">
                        {/* Animated strikethrough price (original monthly) */}
                        {showStrikethrough && (
                          <AnimatePresence mode="popLayout">
                            <motion.span
                              key={`strike-${plan.id}-${isMMonthly}`} // stable key
                              className="price text-xs font-semibold mb-0 line-through text-gray-400 mr-2 text-center flex items-center"
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 10 }}
                              transition={{ duration: 0.3 }}
                            >
                              <span className="text-xs font-semibold">₹</span>
                              {/* {originalMonthlyPrice.toLocaleString("en-IN")} */}
                              {Math.floor(
                                Number(originalMonthlyPrice),
                              ).toLocaleString("en-IN")}
                            </motion.span>
                          </AnimatePresence>
                        )}
                        {/* Current price (or special Elite button) */}
                        <AnimatePresence mode="popLayout">
                          <motion.span
                            key={`price-${plan.id}-${isMMonthly}-${isOpenTab}`}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.3 }}
                            className="font-bold text-base text-[#093a5b]"
                          >
                            {isEliteUser ? (
                              isMMonthly ? (
                                <button
                                  className="relative inline-flex items-center font-semibold tracking-wide align-middle text-sm text-center border-none after:content-[''] after:absolute after:h-px after:w-0 hover:after:w-full after:end-0 hover:after:end-auto after:bottom-0 after:start-0 after:duration-500 text-indigo-600 hover:text-indigo-600 after:bg-indigo-600 duration-500"
                                  onClick={() => setIsMMonthly((prev) => !prev)}
                                >
                                  View yearly pricing
                                </button>
                              ) : (
                                formatPrice(currentPrice)
                              )
                            ) : (
                              formatPrice(currentPrice)
                            )}
                          </motion.span>
                        </AnimatePresence>
                        {/* Billing text (only when price is shown) */}
                        <span style={{ paddingLeft: "4px" }}>
                          {!(isEliteUser && isMMonthly) &&
                            currentPrice !== "Custom" &&
                            currentPrice !== "Contact Sales" &&
                            getBillingText()}
                        </span>
                      </span>

                      {isEliteUser ? (
                        isMMonthly ? (
                          <span className="sub-task text-[#093a5b]">
                            Billed Annually
                          </span>
                        ) : (
                          <span className="sub-task  text-[#093a5b]">
                            Billed Annually
                          </span>
                        )
                      ) : currentPrice === "Custom" ? (
                        <span className="sub-task">Billed Annually</span>
                      ) : currentPrice !== "Contact Sales" ? (
                        <span className="sub-task">{`${isMMonthly ? "" : "Billed Annually"} `}</span>
                      ) : null}
                    </p>

                    <a
                      className="main-link-btn py-2 px-5 inline-block font-semibold tracking-wide
                     border align-middle duration-500 text-base text-center bg-indigo-600 hover:bg-indigo-700 border-indigo-600
                      hover:border-indigo-700 text-white rounded-xl"
                      href={
                        plan.cta_link ||
                        (plan.name === "Elite"
                          ? "/contactsales"
                          : "https://app.simplifyte.com/login")
                      }
                    >
                      {plan.cta_text ||
                        (plan.name === "Elite"
                          ? "Talk To Sales"
                          : `Get Started`)}
                    </a>
                  </div>
                )}
              </>
            );
          })}
    </div>
    // <div className="container relative">
    //   {/* Header */}
    //   {/* Toggle and Tabs */}
    //   {/* title-sticky sticky top-0 z-50 bg-white grid lg:grid-cols-4 md:grid-cols-2 mt-6 gap-[20px] py-4 */}
    //   <div>
    //   </div>
    // </div>
  );
}
